import {
    Button,
    DirectoryTable,
    ErrorAlert,
    FormError,
    Modal,
    PageHeader,
    TextInput,
    buttonStyles,
    useFormatters,
    useDisclosure,
    useTextForm,
} from '@lumlab/platform-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { apiRequest } from '@lumlab/platform-product/web';
import { organizationInput, type DirectoryOrganization } from '../contract/index.ts';
import { useDirectory } from './queries.ts';

export function OrganizationsPage() {
    const { t } = useTranslation();
    const format = useFormatters();
    const organizations = useDirectory<DirectoryOrganization>('/api/admin/organizations');
    const { opened, open, close } = useDisclosure();
    return (
        <>
            <PageHeader
                title={t('platform.admin.organizations')}
                description={t('platform.admin.organizationsDescription')}
                actions={<Button onClick={open}>{t('platform.admin.addOrganization')}</Button>}
            />
            <DirectoryTable
                items={organizations.data?.items ?? []}
                total={organizations.data?.total ?? 0}
                loading={organizations.isPending}
                error={organizations.error}
                search={organizations.search}
                onSearchChange={organizations.setSearch}
                page={organizations.page}
                pageSize={organizations.pageSize}
                onPageChange={organizations.setPage}
                columns={[
                    { key: 'name', label: t('platform.admin.name'), render: (organization) => organization.name },
                    { key: 'slug', label: t('platform.admin.slug'), render: (organization) => organization.slug },
                    {
                        key: 'members',
                        label: t('platform.admin.users'),
                        render: (organization) => format.number(organization.members),
                    },
                    {
                        key: 'createdAt',
                        label: t('platform.admin.createdAt'),
                        render: (organization) => format.date(new Date(organization.createdAt)),
                    },
                    {
                        key: 'actions',
                        label: t('platform.admin.actions'),
                        render: (organization) => (
                            <Link
                                className={buttonStyles({ variant: 'light', size: 'xs' })}
                                to={`/admin/products?organizationId=${organization.id}`}
                            >
                                {t('platform.admin.manageProducts')}
                            </Link>
                        ),
                    },
                ]}
            />
            <Modal opened={opened} onClose={close} title={t('platform.admin.addOrganization')}>
                <OrganizationForm onDone={close} />
            </Modal>
        </>
    );
}

function OrganizationForm({ onDone }: { onDone: () => void }) {
    const { t } = useTranslation();
    const client = useQueryClient();
    const form = useTextForm({
        initialValues: { name: '', slug: '' },
        validate: (values) => {
            const result = organizationInput.safeParse(values);
            return result.success
                ? {}
                : Object.fromEntries(
                      result.error.issues.map((issue) => [String(issue.path[0]), t('platform.errors.bad_request')]),
                  );
        },
    });
    const create = useMutation({
        mutationFn: (body: typeof form.values) => apiRequest('/api/admin/organizations', { method: 'POST', body }),
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: ['admin'] });
            form.reset();
            onDone();
        },
    });
    return (
        <form onSubmit={form.onSubmit((values) => create.mutate(values))}>
            <div className="flex flex-col gap-4">
                <FormError message={form.formError} />
                {create.error && <ErrorAlert error={create.error} />}
                <TextInput required label={t('platform.admin.name')} maxLength={200} {...form.getInputProps('name')} />
                <TextInput
                    required
                    label={t('platform.admin.slug')}
                    description={t('platform.admin.slugHelp')}
                    maxLength={100}
                    {...form.getInputProps('slug')}
                />
                <div className="flex flex-wrap items-center gap-4 justify-end">
                    <Button type="submit" loading={create.isPending}>
                        {t('platform.admin.create')}
                    </Button>
                </div>
            </div>
        </form>
    );
}
